import { ContactAdd } from '#app/routes/mocra+/_form+/components/contacts/add.tsx'
import { ContactCard } from '#app/routes/mocra+/_form+/components/contacts/card.tsx'
import { type ContactDraft } from '#app/routes/mocra+/_form+/components/contacts/types.ts'
import { Section } from '#app/routes/mocra+/_form+/components/section.tsx'
import {
	type ContactDisplay,
	type ContactReference,
} from '#app/routes/mocra+/_form+/contacts/reference.ts'
import { type SectionResources } from '#app/routes/mocra+/_form+/section-resources.ts'

type Props = {
	readonly references: ReadonlyArray<ContactReference>
	readonly displays: ReadonlyArray<ContactDisplay>
	readonly resources: SectionResources
	readonly getFieldErrors: (path: string) => readonly string[]
	readonly onAdd: (draft: ContactDraft) => void
	readonly onEdit: (index: number) => void
	readonly onRemove: (index: number) => void
	readonly existingContactDrafts: ReadonlyArray<ContactDraft>
	readonly directorySource: string
	readonly emptyState?: string
}

export function RecordCustodiansSection({
	references,
	displays,
	resources,
	getFieldErrors,
	onAdd,
	onEdit,
	onRemove,
	existingContactDrafts,
	directorySource,
	emptyState = 'No custodians documented yet.',
}: Props) {
	return (
		<Section
			title="Record custodians"
			description="List the people who can retrieve records during an inspection."
			mocraSections={resources.mocraSections}
			templates={resources.templates}
		>
			<div className="space-y-3">
				{references.length > 0 ? (
					references.map((reference, index) => {
						const display = displays[index]
						const hasErrors =
							getFieldErrors(`custodians.${index}.contactId`).length > 0
						const name = display?.name ?? 'Records custodian'
						const title = display?.title ?? 'Records custodian'
						const email = display?.email ?? ''
						const phone = display?.phone ?? ''
						return (
							<ContactCard
								key={`${reference.contactId}-${index}`}
								name={name}
								title={title}
								email={email}
								phone={phone}
								onClick={() => onEdit(index)}
								onRemove={() => onRemove(index)}
								removeLabel="Remove custodian"
								removeConfirmTitle="Remove this custodian?"
								removeConfirmDescription="This custodian will be removed from the list."
								className={
									hasErrors
										? 'border-destructive/40 ring-destructive/40 ring-1'
										: undefined
								}
							/>
						)
					})
				) : (
					<p className="text-muted-foreground text-sm">{emptyState}</p>
				)}
			</div>
			<ContactAdd
				onSubmit={onAdd}
				directorySource={directorySource}
				existingContacts={existingContactDrafts}
			/>
		</Section>
	)
}
